import type {LogItem, LogModel, Step} from './ActionRunLogModel.ts';

export type SearchOptions = {
  caseSensitive: boolean;
  regex: boolean;
};

export type SearchResult = {
  matches: string[]; // LogItem keys, in document order -- never flat indices, see the design spec
  invalidPattern: boolean;
};

type CompiledSearch = {ok: true; test: (text: string) => boolean} |
  {ok: false; reason: 'empty' | 'invalid-regex'};

const SGR_ESCAPE_RE = /\x1b\[[0-9;]*m/g;

function compileSearchMatcher(query: string, options: SearchOptions): CompiledSearch {
  if (query === '') return {ok: false, reason: 'empty'};
  if (options.regex) {
    let re: RegExp;
    try {
      re = new RegExp(query, options.caseSensitive ? '' : 'i');
    } catch {
      return {ok: false, reason: 'invalid-regex'};
    }
    return {ok: true, test: (text) => re.test(text)};
  }
  const needle = options.caseSensitive ? query : query.toLowerCase();
  return {ok: true, test: (text) => (options.caseSensitive ? text : text.toLowerCase()).includes(needle)};
}

// Strips the raw command prefix (e.g. "::error::", "##[error]") and ANSI SGR color/style
// sequences before matching -- mirrors createLogLineMessage's prefix stripping (ActionRunView.ts)
// without its DOM/label work. Deliberately does NOT synthesize the rendered "Error:"/"Warning:"
// label text -- see the design spec's Non-goals.
function searchableLineText(item: Extract<LogItem, {kind: 'line'}>): string {
  const withoutPrefix = item.cmd ? item.line.message.substring(item.cmd.prefix.length) : item.line.message;
  return withoutPrefix.replace(SGR_ESCAPE_RE, '');
}

export function searchLog(model: LogModel, steps: Step[], query: string, options: SearchOptions): SearchResult {
  const compiled = compileSearchMatcher(query, options);
  if (!compiled.ok) return {matches: [], invalidPattern: compiled.reason === 'invalid-regex'};
  const matches: string[] = [];
  for (const item of model.getFlat()) {
    if (item.kind === 'step-header') {
      const summary = steps[item.step]?.summary;
      if (summary !== undefined && compiled.test(summary)) matches.push(item.key);
    } else if (compiled.test(searchableLineText(item))) {
      matches.push(item.key);
    }
  }
  return {matches, invalidPattern: false};
}
